import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { FlickeringBadge } from '~/components/elements/FlickeringBadge'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { compact, ensureExists, findByPath, isAssigned } from '~/utils'

import { dialogInfoAtom, linkCustomerAttributesInfoAtom } from '../../atoms'
import { useMainQuerySurveys } from '../../hooks/useMainQuery'

import { type InputValues } from './utils'

export type LinkCustomerAttributesButtonProps = {
  index: number
}

export const LinkCustomerAttributesButton = ({
  index,
}: LinkCustomerAttributesButtonProps) => {
  const { t } = useTranslation()
  const { control, getValues } = useFormContext<InputValues>()
  const attachedCustomerAttributeGroupMap = useWatch({
    control,
    name: `customerAttributeGroups.${index}.attachedCustomerAttributeGroupMap`,
  })
  const customerAttributes = useWatch({
    control,
    name: `customerAttributeGroups.${index}.customerAttributes`,
  })

  const isButtonShown = React.useMemo(
    () => R.values(attachedCustomerAttributeGroupMap).every(isAssigned),
    [attachedCustomerAttributeGroupMap],
  )

  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveys = useMainQuerySurveys()
  const hasDifferences = React.useMemo(() => {
    if (!isButtonShown) {
      return false
    }

    const customerAttributeGroups = R.pipe(
      dialogInfo?.surveyGroup?.surveys,
      ensureExists,
      R.map((surveyGroupSurvey) => {
        const attachedCustomerAttributeGroupId =
          attachedCustomerAttributeGroupMap[surveyGroupSurvey.id]
        if (typeof attachedCustomerAttributeGroupId !== 'string') {
          return undefined
        }

        const survey = findByPath(surveys, 'id', surveyGroupSurvey.id)
        const customerAttributeGroup = findByPath(
          survey?.customerAttributeGroups ?? [],
          'id',
          attachedCustomerAttributeGroupId,
        )

        return customerAttributeGroup
      }),
      compact,
    )

    const customerAttributes = customerAttributeGroups.map(
      R.prop('customerAttributes'),
    )
    const [firstCustomerAttributes, ...restCustomerAttributes] =
      customerAttributes

    return !restCustomerAttributes.every(R.isDeepEqual(firstCustomerAttributes))
  }, [
    attachedCustomerAttributeGroupMap,
    dialogInfo?.surveyGroup,
    isButtonShown,
    surveys,
  ])

  const isNotResolvedDifferences = React.useMemo(
    () => hasDifferences && customerAttributes.length === 0,
    [customerAttributes.length, hasDifferences],
  )

  const setLinkCustomerAttributesInfo = useSetAtom(
    linkCustomerAttributesInfoAtom,
  )
  const handleClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    const customerAttributeGroup = getValues(`customerAttributeGroups.${index}`)
    setLinkCustomerAttributesInfo({ index, customerAttributeGroup })
  }, [getValues, index, setLinkCustomerAttributesInfo])

  if (!isButtonShown) {
    return null
  }

  return (
    <StyledButton fillMode="outline" onClick={handleClick}>
      {isNotResolvedDifferences
        ? t('please_correct_inconsistencies_in_labels')
        : hasDifferences
          ? t('inconsistent_labels_resolved')
          : t('link_customized_segments')}

      {isNotResolvedDifferences && <FlickeringBadge color="error" />}
    </StyledButton>
  )
}
